/** Shapes of the `GET /plan/today` response, shared by the pages that read it. */

import { apiFetch } from './client';

export type ContextState = 'locked' | 'available' | 'in_progress' | 'passed';

export interface Prerequisite {
  context_id: string;
  context_name: string;
}

export type LessonState = 'completed' | 'available' | 'locked';

export interface PlanLesson {
  lesson_id: string;
  /** 1-based position of the lesson within its context. */
  order: number;
  state: LessonState;
}

export interface PlanContext {
  context_id: string;
  context_name: string;
  order_index: number;
  assessment_available?: boolean;
  state: ContextState;
  passed_at?: string | null;
  lessons_completed: number;
  lessons_total: number;
  /** The context's lessons, in order. */
  lessons: PlanLesson[];
  /** Order of the first lesson not yet completed; null when all are done. */
  next_lesson_order: number | null;
  /** Whether a check-in now counts toward passing (all lessons completed). */
  assessment_counts_toward_pass: boolean;
  missing_prerequisites: Prerequisite[];
  /** Group the situation is browsed under; null when the content gives none. */
  theme?: string | null;
  /** Learner-facing one-line summary. */
  blurb?: string;
  /** The check-in has been passed. */
  check_in_passed?: boolean;
}

export interface CompletedContext {
  context_id: string;
  context_name: string;
  completed_at: string;
}

export interface PlanTrack {
  track_id: string;
  title: string;
  /** BCP-47 locale of the language the track teaches. */
  locale: string;
  last_completed_context?: CompletedContext;
  contexts: PlanContext[];
}

export type ReadyKind = 'lesson' | 'check_in' | 'review';

export type ReasonCode =
  | 'check_in_finishes_situation'
  | 'continue_situation'
  | 'review_due'
  | 'review_large'
  | 'next_situation';

/** One thing the learner can do now, as ranked by the server (BR-029). */
export interface ReadyItem {
  kind: ReadyKind;
  reason: ReasonCode;
  minutes: number;
  context_id: string | null;
  context_name: string | null;
  lesson_id: string | null;
  lesson_order: number | null;
  phrase_count: number | null;
}

/** One language in the learner's menu: one they have started, plus the current one. */
export interface PlanLanguage {
  locale: string;
  current: boolean;
  /** Situations passed; shown as a count, never a score (C-02). */
  passed_count: number;
  total_count: number;
}

export interface PlanTodayResponse {
  tracks: PlanTrack[];
  /** The language Today is about; null until a learner with several to choose from picks one. */
  current_locale?: string | null;
  /** The language menu. */
  languages?: PlanLanguage[];
  /** Other locales that have tracks (Add a language / Pick a language). */
  available_languages?: string[];
  all_complete: boolean;
  /** The one next step; null when nothing is ready. */
  up_next?: ReadyItem | null;
  /** Up to three other things that are ready. */
  also_ready?: ReadyItem[];
  /** Phrases waiting for practice. */
  review_due?: number;
}

/** Whether the learner can start (or continue) the context now. */
export const isStartable = (context: Pick<PlanContext, 'state'>): boolean =>
  context.state === 'available' || context.state === 'in_progress';

/** Make `locale` the learner's current language (PUT /plan/language). Throws if the server refuses. */
export async function setLanguage(locale: string): Promise<void> {
  const response = await apiFetch('/plan/language', {
    method: 'PUT',
    body: JSON.stringify({ locale }),
  });
  if (!response.ok) {
    throw new Error(`Could not switch language (${response.status})`);
  }
}
